import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button, Form, Modal } from 'react-bootstrap';
import { OrgStatementTinyMceField } from '../../../../admin/organizations/details/OrgStatementTinyMceField';
import { zynity_api } from '../../../../../config/external-routes';
import { DocumentLibraryPickerItem, DocumentLibraryPickerModal } from '../../../../ui/tools/DocumentLibraryPickerModal';

/**
 * Shared modal shape confirmed live across Category/Discussion-topic/Details-item
 * create+edit surfaces (§2.5 of the migration plan) — one reusable component instead
 * of several bespoke ones. Story is TinyMCE (confirmed live, §7.1), reusing the
 * existing generic `OrgStatementTinyMceField` rather than a near-duplicate wrapper.
 * Email Members is deliberately never built — confirmed dead code in production
 * (§13/§10 Phase 7 of the plan). `showAttachments` (Phase 6, §10) adds Yii's inline
 * "type several, submit together" Links/Uploads fields. `showLibrary` (extended
 * 2026-08-27, closing a Phase 7 scope reduction) adds Yii's inline Library field the
 * same way — used for Details items (New Details Item/Edit Item), which Category/
 * Discussion-topic creation deliberately still don't use (their Library lives in the
 * persistent `CategoryAttachments.tsx` panel instead, per Phase 7's writeup).
 * `existingLinks`/`existingUploads` are shown read-only (Yii lets you remove them
 * inline too, but that capability already exists one click away via
 * `CommentsAndAttachments.tsx`'s expand panel — deliberately not duplicated here to
 * avoid a second, harder-to-keep-in-sync delete path for the same rows).
 */
export type ContentEditorValues = {
    title: string;
    description: string;
    date?: string;
    links?: string[];
    uploadedFileNames?: string[];
    libraryItemIds?: number[];
};

type ContentEditorModalProps = {
    show: boolean;
    title: string;
    titleLabel?: string;
    titleRequiredSuffix?: string;
    titlePlaceholder?: string;
    storyLabel?: string;
    storyPlaceholder?: string;
    linksLabel?: string;
    linksPlaceholder?: string;
    addLinkLabel?: string;
    uploadsLabel?: string;
    submitLabel?: string;
    initialValues?: Partial<ContentEditorValues>;
    saving?: boolean;
    showDate?: boolean;
    dateLabel?: string;
    showAttachments?: boolean;
    showLibrary?: boolean;
    initialLibraryItems?: DocumentLibraryPickerItem[];
    existingLinks?: { id: number; url: string }[];
    existingUploads?: { id: number; fileName: string; displayName: string }[];
    onHide: () => void;
    onSubmit: (values: ContentEditorValues) => void;
};

export const ContentEditorModal = ({
    show,
    title,
    titleLabel = 'Title',
    titleRequiredSuffix = ' *',
    titlePlaceholder,
    storyLabel = 'Story',
    storyPlaceholder,
    linksLabel = 'Links',
    linksPlaceholder = 'https://...',
    addLinkLabel = 'Add Link',
    uploadsLabel = 'Uploads',
    submitLabel = 'Post',
    initialValues,
    saving = false,
    showDate = false,
    dateLabel = 'Target date',
    showAttachments = false,
    showLibrary = false,
    initialLibraryItems = [],
    existingLinks = [],
    existingUploads = [],
    onHide,
    onSubmit,
}: ContentEditorModalProps) => {
    const [titleValue, setTitleValue] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState('');
    const [links, setLinks] = useState<string[]>([]);
    const [newLinkUrl, setNewLinkUrl] = useState('');
    const [uploadedFiles, setUploadedFiles] = useState<{ fileName: string; displayName: string }[]>([]);
    const [uploading, setUploading] = useState(false);
    const [libraryItems, setLibraryItems] = useState<DocumentLibraryPickerItem[]>([]);
    const [showLibraryPicker, setShowLibraryPicker] = useState(false);

    useEffect(() => {
        if (!show) {
            return;
        }
        setTitleValue(initialValues?.title ?? '');
        setDescription(initialValues?.description ?? '');
        setDate(initialValues?.date ?? '');
        setLinks([]);
        setNewLinkUrl('');
        setUploadedFiles([]);
        setLibraryItems(initialLibraryItems);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, initialValues]);

    const addLink = () => {
        if (newLinkUrl.trim() === '') {
            return;
        }
        setLinks((current) => [...current, newLinkUrl.trim()]);
        setNewLinkUrl('');
    };

    const removeLink = (index: number) => {
        setLinks((current) => current.filter((_, i) => i !== index));
    };

    const addFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file) {
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file, file.name);
            formData.append('storage_name', `${Date.now()}_new_${Math.random().toString(36).slice(2, 8)}_${file.name}`);
            const response = await axios.post(zynity_api.global_upload_temp_files, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setUploadedFiles((current) => [...current, { fileName: response.data.file_name, displayName: file.name }]);
        } catch (error) {
            // Swallowed here deliberately — the modal has no Swal import today and this
            // is a rare failure mode (temp-upload endpoint down); the file simply won't
            // appear in the pending list, which is visible feedback enough on Post.
        } finally {
            setUploading(false);
        }
    };

    const removeFile = (index: number) => {
        setUploadedFiles((current) => current.filter((_, i) => i !== index));
    };

    const handleSubmit = () => {
        if (titleValue.trim() === '') {
            return;
        }
        onSubmit({
            title: titleValue.trim(),
            description: description.trim(),
            ...(showDate ? { date } : {}),
            ...(showAttachments ? { links, uploadedFileNames: uploadedFiles.map((f) => f.fileName) } : {}),
            ...(showLibrary ? { libraryItemIds: libraryItems.map((item) => item.id) } : {}),
        });
    };

    return (
        <Modal show={show} onHide={onHide}>
            <Modal.Header closeButton className="strategies-modal-header">
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="text-muted small">Fields with * are required.</p>
                <Form.Group className="mb-3">
                    <Form.Label>{titleLabel}{titleRequiredSuffix}</Form.Label>
                    <Form.Control
                        type="text"
                        value={titleValue}
                        onChange={(e) => setTitleValue(e.target.value)}
                        placeholder={titlePlaceholder ?? titleLabel}
                        autoFocus
                    />
                </Form.Group>
                <Form.Group className="mb-3">
                    <Form.Label>{storyLabel}</Form.Label>
                    <OrgStatementTinyMceField
                        id="content-editor-story"
                        name="description"
                        value={description}
                        height={200}
                        placeholder={storyPlaceholder}
                        onChange={(_name, value) => setDescription(value)}
                    />
                </Form.Group>
                {showDate && (
                    <Form.Group className="mb-3">
                        <Form.Label>{dateLabel}</Form.Label>
                        <Form.Control
                            type="date"
                            style={{ maxWidth: '200px' }}
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                        />
                    </Form.Group>
                )}
                {showLibrary && (
                    <Form.Group className="mb-3">
                        <Form.Label>Library</Form.Label>
                        <div className="small text-muted mb-2">Select existing library-only documents for this record.</div>
                        {libraryItems.length > 0 ? (
                            <ul className="ws-v1-detail-library-list small">
                                {libraryItems.map((item) => (
                                    <li key={item.id} className="d-flex justify-content-between align-items-center">
                                        <span>{item.title}</span>
                                        <Button
                                            size="sm"
                                            variant="outline-danger"
                                            onClick={() => setLibraryItems((current) => current.filter((entry) => entry.id !== item.id))}
                                        >
                                            Remove
                                        </Button>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-muted small mb-2">No library-only documents selected.</div>
                        )}
                        <Button size="sm" className="btn-teal" onClick={() => setShowLibraryPicker(true)}>
                            Choose from Library
                        </Button>
                        <DocumentLibraryPickerModal
                            show={showLibraryPicker}
                            onHide={() => setShowLibraryPicker(false)}
                            onSelectItem={(item) => {
                                if (!libraryItems.some((existing) => existing.id === item.id)) {
                                    setLibraryItems((current) => [...current, item]);
                                }
                                setShowLibraryPicker(false);
                            }}
                            selectedItemIds={libraryItems.map((item) => item.id)}
                        />
                    </Form.Group>
                )}
                {showAttachments && (
                    <>
                        <Form.Group className="mb-3">
                            <Form.Label>{linksLabel}</Form.Label>
                            {/* Matches Yii's _formNewEditCategory.php: each link is its own
                                full-width input box with a small delete affordance beside it
                                — not a bulleted list with a big button, which wraps badly for
                                long URLs. */}
                            {existingLinks.length > 0 && (
                                <div className="d-flex flex-column gap-2 mb-2">
                                    {existingLinks.map((link) => (
                                        <Form.Control key={link.id} size="sm" readOnly value={link.url} />
                                    ))}
                                </div>
                            )}
                            {links.length > 0 && (
                                <div className="d-flex flex-column gap-2 mb-2">
                                    {links.map((link, index) => (
                                        <div key={index} className="d-flex align-items-center gap-2">
                                            <Form.Control size="sm" readOnly value={link} />
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                className="flex-shrink-0"
                                                aria-label="Remove link"
                                                title="Delete"
                                                onClick={() => removeLink(index)}
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <div className="d-flex gap-2">
                                <Form.Control
                                    size="sm"
                                    placeholder={linksPlaceholder}
                                    value={newLinkUrl}
                                    onChange={(e) => setNewLinkUrl(e.target.value)}
                                />
                                <Button size="sm" className="btn-teal" disabled={newLinkUrl.trim() === ''} onClick={addLink}>
                                    {addLinkLabel}
                                </Button>
                            </div>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>{uploadsLabel}</Form.Label>
                            {existingUploads.length > 0 && (
                                <div className="d-flex flex-column gap-2 mb-2">
                                    {existingUploads.map((upload) => (
                                        <Form.Control key={upload.id} size="sm" readOnly value={upload.displayName} />
                                    ))}
                                </div>
                            )}
                            {uploadedFiles.length > 0 && (
                                <div className="d-flex flex-column gap-2 mb-2">
                                    {uploadedFiles.map((file, index) => (
                                        <div key={index} className="d-flex align-items-center gap-2">
                                            <Form.Control size="sm" readOnly value={file.displayName} />
                                            <Button
                                                size="sm"
                                                variant="outline-danger"
                                                className="flex-shrink-0"
                                                aria-label="Remove file"
                                                title="Delete"
                                                onClick={() => removeFile(index)}
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                            <Form.Control size="sm" type="file" disabled={uploading} onChange={addFile} />
                        </Form.Group>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>
                    Cancel
                </Button>
                <Button className="btn-teal" onClick={handleSubmit} disabled={saving || titleValue.trim() === ''}>
                    {saving ? 'Saving...' : submitLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};
